import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  Slider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  Face as FaceIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Videocam as WebcamIcon,
  CameraAlt as CaptureIcon,
  Cameraswitch as SwitchCameraIcon,
  LinkedCamera as IPCameraIcon,
  ZoomIn as ZoomInIcon,
  RotateRight as RotateIcon,
  Refresh as RefreshIcon,
  ContentPaste as PasteIcon,
  ContentCut as CutIcon,
} from '@mui/icons-material';
import Cropper, { Area } from 'react-easy-crop';
import { detectFace, calculateFaceCropArea, convertToEasyCropFormat, FaceDetectionResult } from '../utils/faceDetection';
import { selectionToCrop, cropQuadToContainerPoints } from '../utils/selectionCrop';
import { imageAreaFromBoundingBox, boundingBoxAreaFromImage } from '../utils/rotatedArea';
import { outputSizeFor } from '../utils/photoOutputSize';
import { previewBoxFor } from '../utils/previewBox';
import { getCroppedImg, fileToDataUrl, createImage, padImageWithBlack, dataUrlToPhotoOriginal, remoteImageToDataUrl } from '../utils/imageUtils';
import { useWebcam } from '../hooks/useWebcam';
import { useIPCamera, IPCameraConfig } from '../hooks/useIPCamera';
import { isElectron } from '../utils/platform';
import api from '../services/api';
import { useOperationalSettings } from '../contexts/OperationalSettingsContext';
import type { PhotoCaptureResult } from '../types/photo';

// Padding added around every uploaded image so the user can zoom out past
// the image boundary and see black bars.
// 0.5 = 50 % on each side → padded image is 2× the original.
// ZOOM_FOR_ORIGINAL_FILL is the zoom level at which the original (unpadded)
// photo exactly fills the crop circle — used as the default starting zoom.
const PADDING_RATIO = 0.5;
const ZOOM_FOR_ORIGINAL_FILL = 1 + 2 * PADDING_RATIO; // = 2.0
const CROPPER_MAX_ZOOM = 8;

// Re-exported from the one shared definition (src/types/photo.ts).
export type { PhotoCaptureResult };

export interface PhotoCaptureModalEnhancedProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PhotoCaptureResult) => void | Promise<void>;
  title?: string;
  existingPhoto?: string;
  customerId?: string;
  initialTab?: 0 | 1 | 2;
  cropShape?: 'round' | 'rect'; // 'round' for faces, 'rect' for documents
  aspectRatio?: number; // custom aspect ratio for rect crop (default: 3/2 for rect, 1 for round)
  filterCategory?: 'captura_facial' | 'documento' | 'veiculo'; // filter IP cameras by category
  hideIPCamera?: boolean; // hide IP camera option entirely (e.g. for non-person photos)
  /**
   * No face to find — a vehicle, a pet, a document. Hides "Detectar Rosto" and the automatic
   * detection that runs on load, both of which are noise on a photo of a car.
   */
  disableFaceDetection?: boolean;
  /**
   * The caller captured the photo and only wants the EDITOR.
   *
   * The webcam and IP-camera modals each carried their own crop UI, so which editor an operator
   * got depended on how the photo was taken. Delegating those modals wholesale was tried and was
   * wrong — the IP modal owns capture logic that exists nowhere else (ffmpeg/RTSP through
   * Electron, plus an MJPEG-via-canvas path for the browser) — so capture stays with the host and
   * only this editor is shared.
   */
  editOnly?: boolean;
  /** Where "escolher outra foto" goes when a host owns capture. */
  onRetake?: () => void;
  /** The host's wording for that action — "Tirar outra foto" in a webcam modal. */
  retakeLabel?: string;
  /** How the host captured it, stamped on the result. */
  captureSource?: 'file' | 'webcam' | 'ip_camera';
  /**
   * Dark chrome. Defaults to the app's light theme — this editor was hardcoded dark and opened
   * as a black slab over a white screen wherever it was used.
   */
  darkMode?: boolean;
}

interface RegistrationCamera {
  id: string;
  name: string;
  ip_address: string;
  port: number;
  protocol: 'rtsp' | 'http' | 'mjpeg';
  username?: string;
  password?: string;
  rtsp_path?: string;
  snapshot_path?: string;
  status: 'online' | 'offline' | 'unknown';
}

const PhotoCaptureModalEnhanced: React.FC<PhotoCaptureModalEnhancedProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Foto de Identificação',
  existingPhoto,
  customerId,
  initialTab = 0,
  cropShape = 'rect',
  aspectRatio,
  filterCategory = 'captura_facial',
  hideIPCamera = false,
  disableFaceDetection = false,
  editOnly = false,
  onRetake,
  retakeLabel,
  captureSource,
  darkMode = false,
}) => {
  const { settings } = useOperationalSettings();

  /**
   * The editor's chrome. Every surface below used to be a hardcoded dark hex, so it opened as a
   * black slab in the middle of a white application.
   *
   * The canvas behind the photo stays dark in BOTH themes on purpose: it is the backdrop a
   * photograph is judged against, and a white one washes out light images.
   */
  const ui = darkMode
    ? { onAccent: '#ffffff', stage: '#1a1a1a', surface: '#212121', panel: '#1a1a1a', text: 'grey.300', textStrong: 'white', textSoft: 'grey.400', border: 'rgba(255, 255, 255, 0.12)', hover: 'rgba(255, 255, 255, 0.08)' }
      // `stage` stays dark in BOTH themes on purpose: a photograph is judged against its
      // backdrop, and a white one washes out light images.
      // `onAccent` is white in BOTH themes: it sits on a saturated button, never on a panel.
    : { onAccent: '#ffffff', stage: '#1a1a1a', surface: '#ffffff', panel: '#f5f5f5', text: 'grey.800', textStrong: 'grey.900', textSoft: 'grey.600', border: 'rgba(0, 0, 0, 0.12)', hover: 'rgba(0, 0, 0, 0.04)' };

  // Tab state: 0 = file, 1 = webcam, 2 = ip_camera
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(initialTab);

  // Image processing state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  /**
   * The ratio the operator drew with "Selecionar Área", once they have drawn one.
   *
   * Their rectangle used to be squeezed back into the modal's fixed ratio, so selecting a wide
   * strip of a photo did not produce a wide crop. Null until they select; the prop decides until
   * then, and a new image clears it.
   */
  const [selectedAspect, setSelectedAspect] = useState<number | null>(null);
  /**
   * The region "Selecionar Área" marked, in padded-image pixels.
   *
   * A region, not a cut. Cutting replaced the working image with the selection, so everything
   * around it became black padding and the operator lost the context they were working from —
   * they could no longer see the car the plate belonged to. The image is never modified now; this
   * is what gets previewed and saved, and it is drawn back onto the photo as a marker.
   */
  //
  // Held in the IMAGE's own coordinates, which rotation does not touch. `croppedAreaPixels` lives
  // in the ROTATED BOUNDING BOX, so a selection stored in that space stops pointing at its own
  // pixels the moment the operator turns the photo — the marker stayed put while the car rotated
  // underneath it. See utils/rotatedArea.
  const [selectedImageArea, setSelectedImageArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // The ratio the cropper's own window is showing. Everything that measures a drag must use
  // THIS: the selection maths used a hardcoded 3/2 while the cropper showed `aspectRatio`, so on
  // a 4/5 identity photo the region selected and the region saved were different rectangles.
  //
  // A free selection deliberately does NOT reshape this. What gets saved follows the selection,
  // so moving the window would only shift the photo under the operator for nothing.
  const effectiveAspect = aspectRatio ?? (cropShape === 'rect' ? 3 / 2 : 1);

  const [rotation, setRotation] = useState(0);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [isDetectingFace, setIsDetectingFace] = useState(false);
  const [faceDetectionResult, setFaceDetectionResult] = useState<FaceDetectionResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // IP Camera state
  const [ipCameras, setIPCameras] = useState<RegistrationCamera[]>([]);
  const [selectedIPCamera, setSelectedIPCamera] = useState<string>('');
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);

  const [bgColor, setBgColor] = useState<'#000000' | '#ffffff'>('#000000');
  const origDataUrlRef = useRef<string | null>(null);
  const paddedImageDimsRef = useRef<{ width: number; height: number } | null>(null);

  /**
   * The selection projected onto the canvas as it is rotated RIGHT NOW — what the cropper and
   * `getCroppedImg` both speak. Recomputed on every rotation change, which is what keeps the
   * marker on the same pixels while the photo turns.
   */
  const selectedArea = useMemo(() => {
    if (!selectedImageArea || !paddedImageDimsRef.current) return null;
    return boundingBoxAreaFromImage(selectedImageArea, paddedImageDimsRef.current, rotation);
  }, [selectedImageArea, rotation]);

  // Select mode (rectangle overlay on the cropper)
  const [isCutMode, setIsCutMode] = useState(false);
  const cutCanvasRef = useRef<HTMLCanvasElement>(null);
  const cropperContainerRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const cutRectRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Webcam hook
  const {
    videoRef,
    isActive: isWebcamActive,
    isLoading: isWebcamLoading,
    isReady: isWebcamReady,
    error: webcamError,
    start: startWebcam,
    stop: stopWebcam,
    capture: captureWebcam,
    availableCameras,
    selectedCamera,
    selectCamera,
  } = useWebcam();

  // IP Camera hook
  const {
    isElectron: isIPCameraAvailable,
    ffmpegAvailable,
    isCapturing: isIPCameraCapturing,
    captureSnapshot: captureIPCameraSnapshot,
    error: ipCameraError,
    clearError: clearIPCameraError,
  } = useIPCamera();

  // Load registration cameras
  useEffect(() => {
    const loadCameras = async () => {
      if (!customerId || !isIPCameraAvailable) return;

      setIsLoadingCameras(true);
      try {
        const response = await api.get(`/registration-cameras?customer_id=${customerId}&category=${filterCategory}`);
        if (response.data.success && response.data.cameras) {
          setIPCameras(response.data.cameras);
          if (response.data.cameras.length > 0 && !selectedIPCamera) {
            setSelectedIPCamera(response.data.cameras[0].id);
          }
        }
      } catch (err) {
        console.error('Error loading registration cameras:', err);
      } finally {
        setIsLoadingCameras(false);
      }
    };

    if (open && activeTab === 2) {
      loadCameras();
    }
  }, [customerId, open, activeTab, isIPCameraAvailable, selectedIPCamera]);

  // Handle tab change
  useEffect(() => {
    if (!open) return;

    // Stop webcam when switching away
    if (activeTab !== 1 && isWebcamActive) {
      stopWebcam();
    }

    // Start webcam when switching to webcam tab (with small delay to ensure video element is mounted)
    if (activeTab === 1 && !isWebcamActive && !isWebcamLoading) {
      const timer = setTimeout(() => {
        startWebcam();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, open, isWebcamActive, isWebcamLoading]); // Removed startWebcam and stopWebcam from deps

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      // Always start with null — processImage will set imageSrc if existingPhoto is provided
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      // A ratio and a region drawn for the last photo must not frame the next one.
      setSelectedAspect(null);
      setSelectedImageArea(null);
      setCroppedAreaPixels(null);
      setCroppedPreview(null);
      setFaceDetectionResult(null);
      setError(null);
      setActiveTab(initialTab ?? 0);
      setIsCutMode(false);
      cutRectRef.current = null;
      origDataUrlRef.current = null;
      paddedImageDimsRef.current = null;
    } else {
      stopWebcam();
      setIsCutMode(false);
    }
  }, [open, stopWebcam, initialTab]);

  // Load existing photo through processImage so it gets padded correctly
  useEffect(() => {
    if (open && existingPhoto) {
      processImage(existingPhoto);
    }
  }, [open, existingPhoto]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate preview when crop changes
  useEffect(() => {
    const generatePreview = async () => {
      if (!imageSrc || !croppedAreaPixels) return;

      try {
        // Rendered at the shape it will be DISPLAYED at, scaled up for sharpness. Same rule as
        // the thumbnail box, so the bitmap and its frame cannot disagree.
        const box = previewBoxFor({ cropShape, aspectRatio, selectionAspect: selectedAspect ?? undefined });
        const previewSize = { width: box.width * 3, height: box.height * 3 };

        // The selection wins when there is one: it is what will be saved, so it is what the
        // operator has to be shown.
        const area = selectedArea ?? croppedAreaPixels;
        const result = await getCroppedImg(
          imageSrc,
          area,
          rotation,
          previewSize
        );
        setCroppedPreview(result.base64);
      } catch (err) {
        console.error('Error generating preview:', err);
      }
    };

    const timeoutId = setTimeout(generatePreview, 100);
    return () => clearTimeout(timeoutId);
  }, [imageSrc, croppedAreaPixels, rotation, cropShape, aspectRatio, selectedArea, selectedAspect]);

  // Process captured/selected image
  const processImage = useCallback(async (dataUrl: string) => {
    setError(null);
    setIsDetectingFace(false);

    try {
      // Pad the image with black borders so the user can zoom out past the
      // image boundary and see black bars (no library-level zoom clamping fight).
      //
      // The pre-crop source has to be bytes, not a URL: re-cropping an existing photo seeds
      // this modal with its URL, and a URL converts to no original at all — which is how a
      // re-crop used to destroy the original kept at first save. Null when it cannot be
      // read back, which degrades to "this photo has no original", never to a failed save.
      origDataUrlRef.current = dataUrl.startsWith('data:') ? dataUrl : await remoteImageToDataUrl(dataUrl);
      const origImg = await createImage(dataUrl);
      const paddedDataUrl = await padImageWithBlack(dataUrl, PADDING_RATIO, bgColor);
      paddedImageDimsRef.current = {
        width: origImg.width + 2 * Math.round(origImg.width * PADDING_RATIO),
        height: origImg.height + 2 * Math.round(origImg.height * PADDING_RATIO),
      };
      setImageSrc(paddedDataUrl);

      // Only auto-detect if enabled in settings — and never for a photo with no face in it.
      if (settings.autoFaceDetection && !disableFaceDetection) {
        setIsDetectingFace(true);
        const detection = await detectFace(origImg);
        setFaceDetectionResult(detection);

        // Always center regardless of face detection result
        setCrop({ x: 0, y: 0 });
        setZoom(ZOOM_FOR_ORIGINAL_FILL);
        setIsDetectingFace(false);
      } else {
        // Default: show the original photo filling the crop circle
        setCrop({ x: 0, y: 0 });
        setZoom(ZOOM_FOR_ORIGINAL_FILL);
        setFaceDetectionResult(null);
      }

      setRotation(0);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Erro ao processar a imagem. Tente novamente.');
      setIsDetectingFace(false);
    }
  }, [settings.autoFaceDetection, disableFaceDetection, bgColor]);

  // Re-pad image when background color changes
  useEffect(() => {
    if (!origDataUrlRef.current || !imageSrc) return;
    const rePad = async () => {
      const paddedDataUrl = await padImageWithBlack(origDataUrlRef.current!, PADDING_RATIO, bgColor);
      setImageSrc(paddedDataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(ZOOM_FOR_ORIGINAL_FILL);
    };
    rePad();
  }, [bgColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle paste event
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    // Only handle paste when modal is open and on file upload tab without existing image
    if (!open || activeTab !== 0 || imageSrc || isDetectingFace) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          // Validate file size (max 10MB)
          if (blob.size > 10 * 1024 * 1024) {
            setError('A imagem deve ter no máximo 10MB.');
            return;
          }

          // Convert to data URL and process
          const reader = new FileReader();
          reader.onload = (event) => {
            const dataUrl = event.target?.result as string;
            processImage(dataUrl);
          };
          reader.readAsDataURL(blob);
        }
        break;
      }
    }
  }, [open, activeTab, imageSrc, isDetectingFace, processImage]);

  // Paste button click handler (uses Clipboard API)
  const handlePasteButtonClick = useCallback(async () => {
    try {
      const items = await (navigator.clipboard as any).read();
      for (const item of items) {
        const imageType = (item.types as string[]).find((t: string) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataUrl = ev.target?.result as string;
            processImage(dataUrl);
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      setError('Nenhuma imagem encontrada na área de transferência.');
    } catch {
      setError('Permissão negada. Use Ctrl+V para colar.');
    }
  }, [processImage]);

  // Add paste event listener when modal is open
  useEffect(() => {
    if (open && activeTab === 0) {
      document.addEventListener('paste', handlePaste);
      return () => {
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [open, activeTab, handlePaste]);

  // Handle file selection
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione uma imagem válida.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('A imagem deve ter no máximo 10MB.');
      return;
    }

    const dataUrl = await fileToDataUrl(file);
    await processImage(dataUrl);

    event.target.value = '';
  }, [processImage]);

  // Handle webcam capture
  const handleWebcamCapture = useCallback(async () => {
    try {
      console.log('Attempting to capture webcam photo...');
      const blob = await captureWebcam();

      if (!blob) {
        console.error('Webcam capture returned null blob');
        setError('Erro ao capturar foto da webcam. Aguarde o vídeo carregar completamente e tente novamente.');
        return;
      }

      console.log('Webcam capture successful, blob size:', blob.size);

      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result as string;
        await processImage(dataUrl);
        stopWebcam();
      };
      reader.onerror = () => {
        console.error('FileReader error');
        setError('Erro ao processar a imagem capturada.');
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error in handleWebcamCapture:', err);
      setError('Erro ao capturar foto da webcam. Tente novamente.');
    }
  }, [captureWebcam, processImage, stopWebcam]);

  // Handle IP camera capture
  const handleIPCameraCapture = useCallback(async () => {
    if (!selectedIPCamera) {
      setError('Selecione uma câmera primeiro.');
      return;
    }

    const camera = ipCameras.find((c) => c.id === selectedIPCamera);
    if (!camera) {
      setError('Câmera não encontrada.');
      return;
    }

    clearIPCameraError();
    setError(null);

    const config: IPCameraConfig = {
      protocol: camera.protocol,
      ipAddress: camera.ip_address,
      port: camera.port,
      username: camera.username,
      password: camera.password,
      rtspPath: camera.rtsp_path,
      snapshotPath: camera.snapshot_path,
    };

    const dataUrl = await captureIPCameraSnapshot(config);
    if (dataUrl) {
      await processImage(dataUrl);
    } else if (ipCameraError) {
      setError(ipCameraError);
    } else {
      setError('Erro ao capturar foto da câmera IP.');
    }
  }, [selectedIPCamera, ipCameras, captureIPCameraSnapshot, processImage, clearIPCameraError, ipCameraError]);

  // Handle auto-detect face
  const handleAutoDetectFace = useCallback(async () => {
    if (!imageSrc) return;

    setIsDetectingFace(true);
    setError(null);

    try {
      const img = await createImage(imageSrc);
      const detection = await detectFace(img);
      setFaceDetectionResult(detection);

      if (detection.detected && detection.box) {
        const cropArea = calculateFaceCropArea(
          detection.box,
          img.width,
          img.height,
          0.5
        );
        // Back to the configured ratio and the full frame: a face crop is only a face crop at
        // the shape the devices enrol, not at whatever rectangle was drawn by hand before this.
        setSelectedAspect(null);
        setSelectedImageArea(null);
        const easyCropFormat = convertToEasyCropFormat(cropArea, img.width, img.height);
        setCrop(easyCropFormat.crop);
        setZoom(easyCropFormat.zoom);
      } else {
        setError('Nenhum rosto detectado na imagem. Ajuste manualmente.');
      }
    } catch (err) {
      console.error('Error detecting face:', err);
      setError('Erro ao detectar rosto. Ajuste manualmente.');
    } finally {
      setIsDetectingFace(false);
    }
  }, [imageSrc]);

  const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Follow the crop's own proportions. This was a hardcoded 400x400, so every crop was
      // STRETCHED into a square on save — the editor showed one shape and the record kept
      // another, which reads as "my edit was not saved".
      const area = selectedArea ?? croppedAreaPixels;
      const outputSize = outputSizeFor(area) ?? { width: 400, height: 400 };
      const result = await getCroppedImg(
        imageSrc,
        area,
        rotation,
        outputSize,
        { horizontal: false, vertical: false },
        bgColor
      );

      const source = captureSource ?? (activeTab === 0 ? 'file' : activeTab === 1 ? 'webcam' : 'ip_camera');

      onConfirm({
        blob: result.blob,
        base64: result.base64,
        width: result.width,
        height: result.height,
        faceDetected: faceDetectionResult?.detected || false,
        faceConfidence: faceDetectionResult?.confidence,
        source,
        cameraId: source === 'ip_camera' ? selectedIPCamera : undefined,
        // The picked file or captured frame, before padding and cropping.
        original: dataUrlToPhotoOriginal(origDataUrlRef.current),
      });
    } catch (err) {
      console.error('Error cropping image:', err);
      setError('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  }, [imageSrc, croppedAreaPixels, selectedArea, rotation, faceDetectionResult, onConfirm, activeTab, captureSource, selectedIPCamera, bgColor]);

  const handleClearImage = () => {
    // The host owns capture in edit-only mode; dropping into this modal's own file picker would
    // strand the operator in a flow the host knows nothing about.
    if (editOnly) {
      onRetake?.();
      return;
    }
    setImageSrc(null);
    // "Escolher outra foto" — a ratio and region drawn for the old photo must not frame the new.
    setSelectedAspect(null);
    setSelectedImageArea(null);
    setCroppedPreview(null);
    setFaceDetectionResult(null);
    setCroppedAreaPixels(null);
    setError(null);

    // Restart webcam if on webcam tab
    if (activeTab === 1) {
      stopWebcam();
      setTimeout(() => {
        startWebcam();
      }, 100);
    }

    // Restart IP camera if on IP camera tab
    if (activeTab === 2) {
      startWebcam();
    }
  };

  // Cut mode: rasterize current crop and enter freehand lasso mode
  const handleEnterCutMode = useCallback(() => {
    if (!imageSrc) return;
    cutRectRef.current = null;
    setIsCutMode(true);
  }, [imageSrc]);

  // Helper: get canvas-space coordinates from a mouse event
  const getCanvasPoint = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = cutCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const handleCutMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = cutCanvasRef.current;
    if (!canvas) return;
    const pt = getCanvasPoint(e);
    isDrawingRef.current = true;
    cutRectRef.current = { x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y };
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [getCanvasPoint]);

  const handleCutMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || !cutRectRef.current) return;
    const canvas = cutCanvasRef.current;
    if (!canvas) return;

    const pt = getCanvasPoint(e);
    cutRectRef.current = { ...cutRectRef.current, x2: pt.x, y2: pt.y };

    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const { x1, y1 } = cutRectRef.current;
    const rx = Math.min(x1, pt.x);
    const ry = Math.min(y1, pt.y);
    const rw = Math.abs(pt.x - x1);
    const rh = Math.abs(pt.y - y1);

    // Semi-transparent overlay (reveals cropper underneath where cleared)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(rx, ry, rw, rh);

    // Dashed border
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 3]);
    ctx.strokeRect(rx + 1, ry + 1, rw - 2, rh - 2);
    ctx.setLineDash([]);

    // Corner handles
    const hs = 6;
    ctx.fillStyle = '#3b82f6';
    [[rx, ry], [rx + rw, ry], [rx, ry + rh], [rx + rw, ry + rh]].forEach(([cx, cy]) => {
      ctx.fillRect(cx - hs / 2, cy - hs / 2, hs, hs);
    });
  }, [getCanvasPoint]);

  const handleCutMouseUp = useCallback(() => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;

    const rect = cutRectRef.current;
    const canvas = cutCanvasRef.current;

    if (!rect || !canvas || !croppedAreaPixels || !paddedImageDimsRef.current) {
      cutRectRef.current = null;
      canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
      setIsCutMode(false);
      return;
    }

    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);

    // canvas.width/height match the cropper container (set in the effect below).
    const result = selectionToCrop({
      selection: rect,
      container: { width: canvas.width, height: canvas.height },
      aspect: effectiveAspect,
      croppedAreaPixels,
    });

    if (!result) {
      // A click, or a drag that missed the photo — leave the crop exactly as it was.
      cutRectRef.current = null;
      setIsCutMode(false);
      return;
    }

    // Remembered, not applied: the image is left exactly as it is, so the rest of the photo stays
    // on screen and the operator can still see what they selected it OUT of.
    setSelectedAspect(result.aspect);
    // Into the image's own coordinates, so rotating afterwards moves the marker with the photo
    // instead of leaving it where the mouse happened to be.
    setSelectedImageArea(
      paddedImageDimsRef.current
        ? imageAreaFromBoundingBox(result.area, paddedImageDimsRef.current, rotation)
        : result.area,
    );

    cutRectRef.current = null;
    setIsCutMode(false);
  }, [croppedAreaPixels, effectiveAspect, rotation]);

  // Size the overlay to the cropper, and draw the chosen region onto it.
  //
  // Redrawn whenever the view changes, because the marker is anchored to the IMAGE, not to the
  // screen: pan or zoom and the same region of the photo is somewhere else in the container. The
  // conversion is cropAreaToContainerRect, the exact inverse of the one that read the drag.
  useEffect(() => {
    if (!isCutMode && !selectedArea) return;
    const timer = setTimeout(() => {
      const canvas = cutCanvasRef.current;
      const container = cropperContainerRef.current;
      if (!canvas || !container) return;
      canvas.width = container.clientWidth || 800;
      canvas.height = container.clientHeight || 600;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!selectedArea || isCutMode) return;

      // Four corners, not a box around them. A selection is a rectangle of the IMAGE, so once
      // the photo is turned it is no longer upright on screen — and the axis-aligned box around
      // it swelled and drifted as the rotation slider moved. Its corners stay the same distance
      // apart and turn with the car.
      const quad = selectedImageArea && paddedImageDimsRef.current
        ? cropQuadToContainerPoints({
            area: selectedImageArea,
            image: paddedImageDimsRef.current,
            rotation,
            container: { width: canvas.width, height: canvas.height },
            aspect: effectiveAspect,
            croppedAreaPixels,
          })
        : null;
      if (!quad) return;

      const trace = () => {
        ctx.beginPath();
        // [topLeft, topRight, bottomLeft, bottomRight] — walk the perimeter.
        ctx.moveTo(quad[0].x, quad[0].y);
        ctx.lineTo(quad[1].x, quad[1].y);
        ctx.lineTo(quad[3].x, quad[3].y);
        ctx.lineTo(quad[2].x, quad[2].y);
        ctx.closePath();
      };

      // Dim everything that is NOT selected, so the rest of the photo stays readable as context
      // rather than being removed. Cutting it away is what lost the operator the car the plate
      // belonged to.
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'destination-out';
      trace();
      ctx.fill();
      ctx.restore();

      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      trace();
      ctx.stroke();

      // What will actually be SAVED.
      //
      // A canvas crop is always upright, so a selection made on a rotated photo is saved as the
      // box around it — bigger than the quad at any angle that is not square. The quad alone was
      // therefore a lie: the operator framed one thing and the Prévia showed another, with
      // nothing on screen to explain the difference. Drawn only when the two differ, so at 0/90/
      // 180/270 — where they coincide exactly — there is no second outline to puzzle over.
      const xs = quad.map((p) => p.x);
      const ys = quad.map((p) => p.y);
      const savedRect = {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      };
      const matchesQuad =
        Math.abs(savedRect.width - Math.hypot(quad[1].x - quad[0].x, quad[1].y - quad[0].y)) < 1 &&
        Math.abs(savedRect.height - Math.hypot(quad[2].x - quad[0].x, quad[2].y - quad[0].y)) < 1;
      if (!matchesQuad) {
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(savedRect.x, savedRect.y, savedRect.width, savedRect.height);
        ctx.restore();
      }
    }, 10);
    return () => clearTimeout(timer);
  }, [isCutMode, selectedArea, selectedImageArea, rotation, effectiveAspect, croppedAreaPixels]);

  // Render left panel (capture area)
  const renderCaptureArea = () => {
    // If we have an image, show the cropper (with optional selection overlay)
    if (imageSrc) {
      return (
        <Box ref={cropperContainerRef} sx={{ position: 'relative', height: '100%', bgcolor: ui.stage }}>
          <Cropper
            key={imageSrc} // Force remount when image changes to prevent DOM errors
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={effectiveAspect}
            cropShape={cropShape}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            // The library default of 1 makes the wheel crawl: a whole photo down to a
            // number plate is one long unbroken scroll.
            zoomSpeed={2.5}
            onCropComplete={handleCropComplete}
            minZoom={0.1}
            maxZoom={CROPPER_MAX_ZOOM}
            restrictPosition={false}
          />
          {/* Selection overlay.
              Stays mounted once a region is chosen, so the marker remains on the photo — the
              image itself is never modified, which is how the rest of it stays visible. Only
              takes the mouse while actually selecting; otherwise the cropper keeps its drag. */}
          {(isCutMode || selectedArea) && (
            <canvas
              ref={cutCanvasRef}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                cursor: isCutMode ? 'crosshair' : 'default',
                pointerEvents: isCutMode ? 'auto' : 'none',
                zIndex: 10,
                userSelect: 'none',
              }}
              onMouseDown={handleCutMouseDown}
              onMouseMove={handleCutMouseMove}
              onMouseUp={handleCutMouseUp}
              onMouseLeave={handleCutMouseUp}
            />
          )}
        </Box>
      );
    }

    // Show capture UI based on active tab
    switch (activeTab) {
      case 0: // File
        return (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: ui.panel,
              p: 4,
            }}
          >
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'grey.700',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 64, color: ui.textSoft, mb: 2 }} />
              <Typography variant="h6" sx={{ color: ui.text, mb: 1 }}>
                Clique para selecionar uma foto
              </Typography>
              <Typography variant="body2" sx={{ color: ui.textSoft }}>
                ou arraste e solte aqui
              </Typography>
            </Box>

            {/* Paste button */}
            <Button
              variant="outlined"
              size="medium"
              startIcon={<PasteIcon />}
              onClick={handlePasteButtonClick}
              sx={{
                mt: 3,
                maxWidth: 400,
                borderColor: 'rgba(25, 118, 210, 0.4)',
                color: 'primary.light',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              Colar Foto (Ctrl+V)
            </Button>
          </Box>
        );

      case 1: // Webcam
        return (
          <Box
            sx={{
              height: '100%',
              position: 'relative',
              bgcolor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Video element - always rendered so ref is available */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // live webcam video stage, not a photo the operator framed
                transform: 'scaleX(-1)',
                backgroundColor: '#000',
                display: 'block',
              }}
              onLoadedMetadata={() => console.log('Video metadata loaded')}
              onCanPlay={() => console.log('Video can play')}
              onPlaying={() => console.log('Video is playing')}
            />

            {/* Loading overlay while starting camera */}
            {isWebcamLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.8)',
                  zIndex: 5,
                }}
              >
                <CircularProgress sx={{ mb: 2, color: ui.onAccent }} />
                <Typography color="white">Iniciando câmera...</Typography>
              </Box>
            )}

            {/* Error overlay */}
            {webcamError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.9)',
                  p: 3,
                  zIndex: 5,
                }}
              >
                <Alert severity="error" sx={{ mb: 2 }}>
                  {webcamError}
                </Alert>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={startWebcam}>
                  Tentar Novamente
                </Button>
              </Box>
            )}

            {/* Capture guide overlay — round for faces, rectangular for documents */}
            {isWebcamReady && !isWebcamLoading && (
              cropShape === 'rect' ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70%',
                    aspectRatio: String(aspectRatio ?? 3 / 2),
                    border: '3px dashed rgba(255,255,255,0.6)',
                    borderRadius: 2,
                    pointerEvents: 'none',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '55%',
                    aspectRatio: '1',
                    border: '3px dashed rgba(255,255,255,0.6)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                  }}
                />
              )
            )}

            {/* Loading indicator while video is buffering */}
            {!isWebcamReady && isWebcamActive && !isWebcamLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress sx={{ color: ui.onAccent, mb: 1 }} />
                <Typography color="white">Aguardando vídeo...</Typography>
              </Box>
            )}

            {/* Capture button */}
            {isWebcamReady && !isWebcamLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 2,
                  zIndex: 10,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CaptureIcon />}
                  onClick={handleWebcamCapture}
                  sx={{
                    // On the dark camera stage, so it stays a white button in both themes.
                    bgcolor: ui.onAccent,
                    color: 'black',
                    '&:hover': { bgcolor: 'grey.200' },
                  }}
                >
                  Capturar
                </Button>

                {availableCameras.length > 1 && (
                  <IconButton
                    onClick={() => {
                      const currentIndex = availableCameras.findIndex(c => c.deviceId === selectedCamera);
                      const nextIndex = (currentIndex + 1) % availableCameras.length;
                      selectCamera(availableCameras[nextIndex].deviceId);
                    }}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: ui.onAccent }}
                  >
                    <SwitchCameraIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        );

      case 2: // IP Camera
        return (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: ui.panel,
              p: 3,
            }}
          >
            {!isIPCameraAvailable ? (
              <Alert severity="info" sx={{ maxWidth: 350 }}>
                A captura via câmera IP está disponível apenas no aplicativo desktop.
              </Alert>
            ) : isLoadingCameras ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress sx={{ mb: 2, color: ui.text }} />
                <Typography color="grey.300">Carregando câmeras...</Typography>
              </Box>
            ) : ipCameras.length === 0 ? (
              <Box sx={{ textAlign: 'center' }}>
                <IPCameraIcon sx={{ fontSize: 64, color: 'grey.600', mb: 2 }} />
                <Typography color="grey.400">
                  Nenhuma câmera de cadastro configurada.
                </Typography>
              </Box>
            ) : (
              <>
                <IPCameraIcon sx={{ fontSize: 80, color: 'grey.600', mb: 2 }} />
                <Typography color="grey.400" sx={{ mb: 3 }}>
                  Selecione uma câmera e clique em Capturar
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isIPCameraCapturing ? <CircularProgress size={20} color="inherit" /> : <CaptureIcon />}
                  disabled={!selectedIPCamera || isIPCameraCapturing}
                  onClick={handleIPCameraCapture}
                >
                  {isIPCameraCapturing ? 'Capturando...' : 'Capturar Foto'}
                </Button>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // Render right panel (controls)
  const renderControlPanel = () => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Controls based on state */}
      {imageSrc ? (
        // Image editing controls
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Face detection status */}
          {isDetectingFace ? (
            <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>
              Detectando rosto...
            </Alert>
          ) : faceDetectionResult ? (
            <Alert
              severity={faceDetectionResult.detected ? 'success' : 'warning'}
              icon={faceDetectionResult.detected ? <CheckIcon /> : <WarningIcon />}
              sx={{ mb: 2 }}
            >
              {faceDetectionResult.detected
                ? `Rosto detectado (${Math.round((faceDetectionResult.confidence || 0) * 100)}%)`
                : 'Nenhum rosto detectado'}
            </Alert>
          ) : null}

          {/* Zoom control */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: ui.textSoft }}>
                <ZoomInIcon sx={{ fontSize: 16, mr: 0.5 }} /> Zoom
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: ui.textSoft, mr: 0.5 }}>Fundo:</Typography>
                <IconButton
                  size="small"
                  onClick={() => setBgColor('#000000')}
                  title="Fundo preto"
                  sx={{
                    width: 20, height: 20, bgcolor: '#000000', borderRadius: 0.5,
                    border: bgColor === '#000000' ? '2px solid #1976d2' : '2px solid #555',
                    '&:hover': { bgcolor: '#222' },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setBgColor('#ffffff')}
                  title="Fundo branco"
                  sx={{
                    width: 20, height: 20, bgcolor: '#ffffff', borderRadius: 0.5,
                    border: bgColor === '#ffffff' ? '2px solid #1976d2' : '2px solid #555',
                    '&:hover': { bgcolor: '#eee' },
                  }}
                />
              </Box>
            </Box>
            <Slider
              value={zoom}
              min={0.1}
              max={CROPPER_MAX_ZOOM}
              step={0.1}
              onChange={(_, v) => setZoom(v as number)}
              size="small"
            />
          </Box>

          {/* Rotation control */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mb: 0.5, color: ui.textSoft }}>
              <RotateIcon sx={{ fontSize: 16, mr: 0.5 }} /> Rotação
            </Typography>
            <Slider
              value={rotation}
              min={-180}
              max={180}
              step={1}
              onChange={(_, v) => setRotation(v as number)}
              size="small"
            />
          </Box>

          {/* Preview */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ color: ui.textSoft }}>
              Prévia:
            </Typography>
            {/* The preview answers "what will be saved", so it has to be that shape. An MUI
                Avatar masks to a circle and centre-crops: a free selection shown through one was
                a different region from the one marked on the photo. See utils/previewBox. */}
            {(() => {
              const box = previewBoxFor({ cropShape, aspectRatio, selectionAspect: selectedAspect ?? undefined });
              if (box.kind === 'circle') {
                return (
                  <Avatar
                    src={croppedPreview || undefined}
                    sx={{ width: box.width, height: box.height, border: '2px solid', borderColor: 'rgba(255, 255, 255, 0.2)' }}
                  />
                );
              }
              return (
                <Box
                  component="img"
                  src={croppedPreview || undefined}
                  sx={{
                    width: box.width,
                    height: box.height,
                    border: '2px solid',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 1,
                    // Stretched, never cut: a free selection rarely matches the frame, and
                    // every pixel the operator chose has to stay on screen.
                    objectFit: 'fill',
                    bgcolor: 'rgba(0, 0, 0, 0.2)'
                  }}
                />
              );
            })()}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* A car, a pet and a document have no face to find. */}
            {!disableFaceDetection && (
            <Button
              variant={settings.autoFaceDetection ? 'outlined' : 'contained'}
              size="small"
              startIcon={isDetectingFace ? <CircularProgress size={14} /> : <FaceIcon />}
              onClick={handleAutoDetectFace}
              disabled={isDetectingFace || isCutMode}
              fullWidth
              sx={!settings.autoFaceDetection ? {
                bgcolor: '#2563eb',
                color: ui.onAccent,
                '&:hover': { bgcolor: '#1d4ed8' },
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)',
              } : undefined}
            >
              Detectar Rosto
            </Button>
            )}
            <Button
              variant={isCutMode ? 'contained' : 'outlined'}
              size="small"
              startIcon={<CutIcon />}
              onClick={isCutMode
                ? () => { setIsCutMode(false); cutRectRef.current = null; }
                : handleEnterCutMode}
              disabled={!croppedAreaPixels && !isCutMode}
              fullWidth
              color={isCutMode ? 'error' : 'primary'}
              sx={isCutMode ? { bgcolor: 'error.dark', '&:hover': { bgcolor: 'error.main' } } : undefined}
            >
              {isCutMode ? 'Cancelar Seleção' : 'Selecionar Área'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleClearImage}
              disabled={isCutMode}
              fullWidth
            >
              {retakeLabel ?? 'Escolher Outra Foto'}
            </Button>
          </Box>
        </Box>
      ) : (
        // Capture mode controls
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Webcam camera selector */}
          {activeTab === 1 && availableCameras.length > 1 && (
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: ui.textSoft, '&.Mui-focused': { color: 'primary.main' } }}>
                Câmera
              </InputLabel>
              <Select
                value={selectedCamera || ''}
                label="Câmera"
                onChange={(e) => selectCamera(e.target.value)}
                sx={{
                  color: ui.text,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '& .MuiSvgIcon-root': {
                    color: ui.textSoft,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#2a2a2a',
                      '& .MuiMenuItem-root': {
                        color: ui.text,
                        '&:hover': {
                          bgcolor: ui.hover,
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(25, 118, 210, 0.12)',
                          '&:hover': {
                            bgcolor: 'rgba(25, 118, 210, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {availableCameras.map((camera, index) => (
                  <MenuItem key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Câmera ${index + 1}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* IP Camera selector */}
          {activeTab === 2 && isIPCameraAvailable && ipCameras.length > 0 && (
            <Box>
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ color: ui.textSoft, '&.Mui-focused': { color: 'primary.main' } }}>
                  Câmera IP
                </InputLabel>
                <Select
                  value={selectedIPCamera}
                  label="Câmera IP"
                  onChange={(e) => setSelectedIPCamera(e.target.value)}
                  disabled={isIPCameraCapturing}
                  sx={{
                    color: ui.text,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '& .MuiSvgIcon-root': {
                      color: ui.textSoft,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#2a2a2a',
                        '& .MuiMenuItem-root': {
                          color: ui.text,
                          '&:hover': {
                            bgcolor: ui.hover,
                          },
                          '&.Mui-selected': {
                            bgcolor: 'rgba(25, 118, 210, 0.12)',
                            '&:hover': {
                              bgcolor: 'rgba(25, 118, 210, 0.2)',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {ipCameras.map((camera) => (
                    <MenuItem key={camera.id} value={camera.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor:
                              camera.status === 'online'
                                ? 'success.main'
                                : camera.status === 'offline'
                                ? 'error.main'
                                : 'grey.400',
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1, color: ui.text }}>
                          {camera.name}
                        </Typography>
                        <Chip
                          label={camera.protocol.toUpperCase()}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', color: ui.textSoft, borderColor: 'grey.600' }}
                          variant="outlined"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!ffmpegAvailable && ipCameras.some((c) => c.protocol === 'rtsp') && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    ffmpeg não encontrado. Câmeras RTSP podem não funcionar.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Tips */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mt: 'auto',
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              borderColor: ui.border,
            }}
          >
            <Typography variant="caption" sx={{ color: ui.text, fontWeight: 'bold' }} display="block" gutterBottom>
              Dicas para uma boa foto:
            </Typography>
            <Typography variant="caption" component="ul" sx={{ color: ui.textSoft, m: 0, pl: 2 }}>
              {cropShape === 'rect' ? (
                <>
                  <li>Documento completamente visível e legível</li>
                  <li>Boa iluminação, sem reflexos</li>
                  <li>Foto de frente, sem ângulos</li>
                </>
              ) : (
                <>
                  <li>Rosto claramente visível e centralizado</li>
                  <li>Boa iluminação, sem sombras</li>
                  <li>Olhe diretamente para a câmera</li>
                </>
              )}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '80vh',
          maxHeight: 600,
          bgcolor: ui.panel,
          color: ui.text,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          bgcolor: ui.surface,
          borderBottom: '1px solid',
          borderColor: ui.border,
        }}
      >
        <Typography variant="h6" component="span" sx={{ color: ui.textStrong }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: ui.textSoft, '&:hover': { color: ui.textStrong } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          overflow: 'hidden',
          bgcolor: ui.panel,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* Left panel - Capture/Cropper area (2/3 width) */}
        <Box
          sx={{
            flex: 2,
            minWidth: 0,
            height: '100%',
            borderRight: 1,
            borderColor: ui.border,
          }}
        >
          {renderCaptureArea()}
        </Box>

        {/* Right panel - Controls (1/3 width) */}
        <Box
          sx={{
            flex: 1,
            minWidth: 250,
            maxWidth: 300,
            height: '100%',
            overflow: 'auto',
            bgcolor: ui.surface,
          }}
        >
          {renderControlPanel()}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          bgcolor: ui.surface,
          borderTop: '1px solid',
          borderColor: ui.border,
        }}
      >
        <Button onClick={onClose} sx={{ color: ui.text, '&:hover': { bgcolor: ui.hover } }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!imageSrc || !croppedAreaPixels || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isProcessing ? 'Processando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoCaptureModalEnhanced;
